import type { LensCardData, LensFilterValues } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';
import { type LensDataReadiness, lensDataReady } from '~/utils/leLensTransport';
import { lensQueriedAtLabel } from '~/utils/lensQueriedAt';

import { type Row, number, ratio, text } from '../CommercialKpiTracker/liveModel';

import {
  type Metric,
  type N,
  type Point,
  type State,
  addDays,
  countries,
  crMarkets,
  crProducts,
  destinations,
  growth,
  isCR,
  metricLabel,
  panelGroupKey,
  products,
  stateSearch,
} from './fidelityModel';
import { metricValue, startOfPeriod, yesterday } from './model';

export type Options = {
  markets: Array<string>;
  products: Array<string>;
  languages: Array<string>;
  groups: Array<string>;
  destinations: Array<string>;
  countryPanels: Array<string>;
  productGroups: Array<string>;
  destinationGroups: Array<string>;
};
export const civilDateValid = (date: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(date) &&
  !Number.isNaN(Date.parse(date)) &&
  new Date(date).toISOString().slice(0, 10) === date;
export type Kpi = { label: string; metric: Metric; basis: 'BR' | 'AR'; current: N; previous: N; growth: N };
export type Dashboard = {
  conversion: boolean;
  valid: boolean;
  ready: boolean;
  stateIdentity: string;
  problem: string | null;
  start: string;
  end: string;
  trendStart: string;
  summaryEnd: string;
  source: string;
  sourceAsOf: string | null;
  /** "Data queried <time> (Sydney)": the Lens fetch time; the source table refresh time is not available. */
  queriedAt: string | null;
  options: Options;
  totalsKnown: boolean;
  kpis: Array<Kpi>;
  overview: Array<Point>;
  panels: Array<{ name: string; points: Array<Point> }>;
  share: Array<Record<string, string | number | null>>;
  shareNames: Array<string>;
};
export const financialColumns = [
  'margin_br',
  'margin_ar',
  'margin_lybr',
  'margin_lyar',
  'sales_br',
  'sales_ar',
  'sales_lybr',
  'sales_lyar',
  'estimated_br',
  'estimated_ar',
  'estimated_lybr',
  'estimated_lyar',
  'aov_sales',
  'ly_aov_sales',
  'orders',
  'orders_ly',
  'customers',
  'customers_ly',
  'aov_orders',
  'ly_aov_orders',
] as const;
export function dependencies(view: State['view']) {
  if (isCR(view)) {
    const suffix = view === 'Countries CR' ? 'website_region' : 'cr_product';
    return ['cr_daily_' + suffix, 'cr_overall_' + suffix, 'cr_summary_' + suffix];
  }
  const suffix =
    view === 'Countries Margin' ? 'country_group' : view === 'Product Margin' ? 'product_group' : 'destination_group';
  return [
    'daily_' + suffix,
    'sales_summary',
    'daily_overall',
    ...(view === 'Product Margin' ? [] : ['daily_country_share']),
  ];
}
export function filterOptions(s: State, options: Record<string, Array<string>>): Options {
  const cr = isCR(s.view);
  return {
    markets: options[cr ? 'website_region' : 'market'] ?? [],
    products: options[cr ? 'cr_product' : 'product'] ?? [],
    languages: options.language ?? [],
    groups: options.country_group ?? [],
    destinations: options.destination ?? [],
    countryPanels: options.country_panel ?? [],
    productGroups: options.product_group ?? [],
    destinationGroups: options.destination_group ?? [],
  };
}
/** None is an empty local selection, applied only after the authenticated response
 * passes the same complete-card gate. It must never turn [] (=unfiltered) into visible All. */
export function scopeValid(s: State, options: Options) {
  const valid = (value: string, domain: Array<string>) =>
    value === 'All' || value === 'None' || value.split('|').every((v) => domain.includes(v));
  if (
    (s.to && !civilDateValid(s.to)) ||
    (s.period === 'Custom' && (!civilDateValid(s.from) || !civilDateValid(s.to) || s.from > s.to))
  ) {
    return false;
  }
  return (
    valid(s.market, options.markets) &&
    valid(s.product, options.products) &&
    valid(s.language, options.languages) &&
    (isCR(s.view) ||
      (valid(s.countryGroup, options.groups) &&
        valid(s.cancelled, ['Yes', 'No']) &&
        valid(s.buyer, ['New', 'Repeat']) &&
        valid(s.destination, options.destinations) &&
        valid(s.countryPanel, options.countryPanels) &&
        valid(s.productGroup, options.productGroups) &&
        valid(s.destinationGroup, options.destinationGroups)))
  );
}
export function countriesRequest(s: State, now: Date): LensFilterValues {
  const to = civilDateValid(s.to) ? s.to : yesterday(now);
  const selection = (value: string) => (value === 'All' || value === 'None' ? [] : value.split('|'));
  const fields: Record<string, string> = isCR(s.view)
    ? { website_region: s.market, cr_product: s.product, language: s.language }
    : {
        market: s.market,
        product: s.product,
        country_group: s.countryGroup,
        cancelled: s.cancelled,
        buyer: s.buyer,
        language: s.language,
        destination: s.destination,
        country_panel: s.countryPanel,
        product_group: s.productGroup,
        destination_group: s.destinationGroup,
      };
  return {
    period: {
      window: { from: s.period === 'Custom' && civilDateValid(s.from) ? s.from : startOfPeriod(s.period, to), to },
      comparison: null,
    },
    ...Object.fromEntries(
      Object.entries(fields)
        .filter(([, value]) => value !== 'All')
        .map(([key, value]) => [key, selection(value)]),
    ),
  };
}
function cardRows(
  cards: ReadonlyArray<LensCardData>,
  id: string,
  dimensions: Array<string>,
  conversion: boolean,
): Array<Row> {
  const card = cards.find((c) => c.cardId === id);
  if (!card) {
    throw new Error('Missing ' + id);
  }
  // Governed queries return a day-grained date under its source column name.
  // Keep the presentation model's grain key without changing the population.
  const rows = (card.rows ?? []).map((row) =>
    dimensions.includes('day(date)') && !('day(date)' in row) && 'date' in row
      ? { ...row, 'day(date)': row.date }
      : row,
  );
  if (card.status === 'empty' && rows.length) {
    throw new Error('Nonempty population marked empty: ' + id);
  }
  if (card.status === 'ready' && !rows.length) {
    throw new Error('Missing rows: ' + id);
  }
  if (card.totalRowCount !== undefined && card.totalRowCount > rows.length) {
    throw new Error('Incomplete population: ' + id);
  }
  const keys = new Set<string>();
  for (const row of rows) {
    for (const column of [...dimensions, ...(conversion ? ['packages', 'views'] : financialColumns)]) {
      if (!(column in row)) {
        throw new Error('Missing ' + id + '.' + column);
      }
    }
    for (const column of conversion ? ['packages', 'views'] : financialColumns) {
      if (row[column] !== null && number(row[column]) === null) {
        throw new Error('Invalid operand ' + id + '.' + column);
      }
    }
    const key = JSON.stringify(dimensions.map((d) => (d === 'day(date)' ? text(row[d]).slice(0, 10) : text(row[d]))));
    if (keys.has(key)) {
      throw new Error('Duplicate aggregate grain: ' + id);
    }
    keys.add(key);
    if (dimensions.includes('day(date)')) {
      const date = text(row['day(date)']).slice(0, 10);
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
        Number.isNaN(Date.parse(date)) ||
        new Date(date).toISOString().slice(0, 10) !== date
      ) {
        throw new Error('Invalid Sydney date: ' + id);
      }
    }
  }
  return rows;
}
function points(rows: Array<Row>, s: State, end: string, conversion: boolean): Array<Point> {
  // The source shifts last-year rows forward a year; stop at the last current day so the axis never runs ahead.
  const start = addDays(end, -89);
  const byDate = new Map(rows.map((row) => [text(row['day(date)']).slice(0, 10), row]));
  const result: Array<Point> = [];
  const value = (date: string, ly = false) => {
    const row = byDate.get(date);
    return row ? metricValue(row, s.metric, s.basis, ly) : null;
  };
  const average = (date: string, ly: boolean) => {
    const known: Array<number> = [];
    for (let i = 0; i < s.dma; i++) {
      const day = addDays(date, -i),
        operand = !ly && day > end ? null : value(day, ly);
      if (operand !== null) {
        known.push(operand);
      }
    }
    return known.length ? known.reduce((a, b) => a + b, 0) / known.length : null;
  };
  for (let date = start; date <= end; date = addDays(date, 1)) {
    const row = byDate.get(date),
      packages = number(row?.packages),
      views = number(row?.views);
    const current = conversion ? ratio(packages, views) : date > end ? null : average(date, false);
    const previous = conversion ? null : average(date, true),
      yoy = growth(current, previous);
    result.push({
      date,
      at: Date.parse(date + 'T00:00:00Z'),
      current,
      previous,
      yoy,
      up: yoy === null || yoy < 0 ? null : yoy,
      down: yoy === null || yoy >= 0 ? null : yoy,
      raw: conversion ? current : date > end ? null : value(date),
      rawLY: conversion ? null : value(date, true),
      ...(conversion ? { packages, views } : {}),
    });
  }
  return result;
}
export function adaptCountries(
  s: State,
  input: LensDataReadiness,
  options: Record<string, Array<string>>,
  now: Date,
): Dashboard {
  const conversion = isCR(s.view),
    end = yesterday(now),
    domain = filterOptions(s, options);
  const valid = scopeValid(s, domain),
    cards = input.cards ?? [],
    required = dependencies(s.view);
  const model: Dashboard = {
    conversion,
    valid,
    ready: false,
    stateIdentity: stateSearch(s),
    problem: null,
    start: s.period === 'Custom' ? s.from : startOfPeriod(s.period, civilDateValid(s.to) ? s.to : end),
    summaryEnd: s.to || end,
    end,
    trendStart: addDays(end, -89),
    source: conversion ? 'analytics.views_with_sales' : 'analytics.revenue_bymarkets_weekly',
    sourceAsOf: null,
    queriedAt: null,
    options: domain,
    totalsKnown: false,
    kpis: [],
    overview: [],
    panels: [],
    share: [],
    shareNames: [],
  };
  if (!valid) {
    model.problem = 'A selected filter value or date is invalid. Clear that selection to continue.';
    return model;
  }
  if (!lensDataReady(input, required, valid)) {
    model.problem = 'Waiting for complete live data for this scope.';
    return model;
  }
  try {
    const suffix = conversion
      ? s.view === 'Countries CR'
        ? 'website_region'
        : 'cr_product'
      : s.view === 'Countries Margin'
        ? 'country_group'
        : s.view === 'Product Margin'
          ? 'product_group'
          : 'destination_group';
    const dimension = conversion
      ? s.view === 'Countries CR'
        ? 'cr_market_group'
        : 'cr_product_group'
      : suffix === 'country_group'
        ? 'country_panel'
        : suffix;
    const dailyId = (conversion ? 'cr_daily_' : 'daily_') + suffix;
    // This is the original None selection predicate, not a fixture/credential fallback.
    // Even a known empty selection requires every live dependency to validate first.
    const salesNone = [
      s.market,
      s.product,
      s.countryGroup,
      s.cancelled,
      s.buyer,
      s.language,
      s.destination,
      s.countryPanel,
      s.productGroup,
      s.destinationGroup,
    ].includes('None');
    const overallNone = conversion ? [s.market, s.product, s.language].includes('None') : salesNone;
    const detailNone = conversion
      ? s.language === 'None' || (s.view === 'Countries CR' ? s.product : s.market) === 'None'
      : salesNone;
    const dailyRows = cardRows(cards, dailyId, ['day(date)', dimension], conversion);
    const overallRows = cardRows(
      cards,
      conversion ? 'cr_overall_' + suffix : 'daily_overall',
      ['day(date)'],
      conversion,
    );
    const summaryRows = cardRows(cards, conversion ? 'cr_summary_' + suffix : 'sales_summary', [], conversion);
    const daily = detailNone ? [] : dailyRows;
    const overall = overallNone ? [] : overallRows;
    const summary = overallNone ? {} : (summaryRows[0] ?? {});
    model.overview = points(overall, s, end, conversion);
    const names = conversion
      ? s.view === 'Countries CR'
        ? crMarkets
        : crProducts
      : s.view === 'Countries Margin'
        ? [...countries, 'Long Tail']
        : s.view === 'Product Margin'
          ? products
          : destinations;
    model.panels = names.map((name) => ({
      name,
      points: points(
        daily.filter((row) => text(row[dimension]) === panelGroupKey(s.view, name)),
        s,
        end,
        conversion,
      ),
    }));
    if (!conversion) {
      const defs: Array<{ metric: Metric; basis: 'BR' | 'AR' }> = [
        { metric: 'sales', basis: 'BR' },
        { metric: 'sales', basis: 'AR' },
        { metric: 'margin', basis: 'BR' },
        { metric: 'margin', basis: 'AR' },
        ...(s.view === 'Countries Margin'
          ? [
              { metric: 'estimated' as const, basis: 'BR' as const },
              { metric: 'estimated' as const, basis: 'AR' as const },
            ]
          : []),
        ...(s.view === 'Product Margin'
          ? [
              { metric: 'orders' as const, basis: 'BR' as const },
              { metric: 'aov' as const, basis: 'BR' as const },
            ]
          : [
              { metric: 'aov' as const, basis: 'BR' as const },
              { metric: 'orders' as const, basis: 'BR' as const },
            ]),
        { metric: 'customers', basis: 'BR' },
      ];
      // Overall COUNTD/AOV must come from the overall card, never from summing panels.
      model.kpis = defs.map((def) => {
        const current = metricValue(summary, def.metric, def.basis),
          previous = metricValue(summary, def.metric, def.basis, true);
        // AoV YoY compares the tile's own AoV with last year's on the same exclusions (no surcharges or
        // service fees). Tableau's Change AoV misspells 'surchage', so it never excluded LY surcharges.
        return { ...def, label: metricLabel(def), current, previous, growth: growth(current, previous) };
      });
      const shareDimension = s.view === 'Product Margin' ? 'product_group' : 'country_group';
      const liveShareRows = cardRows(
        cards,
        s.view === 'Product Margin' ? 'daily_product_group' : 'daily_country_share',
        ['day(date)', shareDimension],
        false,
      );
      const shareRows = salesNone ? [] : liveShareRows;
      model.shareNames = [
        ...new Set(
          shareRows.filter((r) => text(r['day(date)']).slice(0, 10) <= end).map((r) => text(r[shareDimension])),
        ),
      ].sort((a, b) => a.localeCompare(b));
      const series = model.shareNames.map((name) =>
        points(
          shareRows.filter((row) => text(row[shareDimension]) === name),
          s,
          end,
          false,
        ),
      );
      model.share = model.overview
        .filter((p) => p.date <= end)
        .map((p, i) => ({
          date: p.date,
          at: p.at,
          ...Object.fromEntries(model.shareNames.map((name, j) => [name, ratio(series[j][i].current, p.current)])),
        }));
    }
    model.queriedAt = lensQueriedAtLabel(cards.filter((c) => required.includes(c.cardId)));
    model.totalsKnown = true;
    model.ready = true;
  } catch (error) {
    model.problem = error instanceof Error ? error.message : 'Incomplete Countries response.';
  }
  return model;
}
const MONEY_DECIMALS = 2;
const RATIO_DECIMALS = 4;
/** Export at displayed precision: money (and moving-average counts) to the cent, ratios as 4-dp fractions. */
export function rounded(value: N | undefined, decimals: number): N {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return value ?? null;
  }
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
/** User-facing scope of an export: the visible selections only, not UI-only state (theme, layout, highlight). */
// The CR views are an unsmoothed packages ÷ views ratio; these selections don't reach the CR request.
const SALES_ONLY_KEYS: ReadonlyArray<keyof State> = ['dma', 'cancelled', 'buyer', 'metric', 'basis'];

export function exportScope(s: State, d: Dashboard): Array<string> {
  const all: Array<keyof State> = [
    'view',
    'period',
    ...(s.period === 'Custom' ? (['from', 'to'] as const) : []),
    'dma',
    'market',
    'product',
    'countryGroup',
    'cancelled',
    'buyer',
    'language',
    'metric',
    'basis',
    // Panel filters only reach the page through links or AskRichard; list them when they narrow the data.
    ...(['destination', 'countryPanel', 'productGroup', 'destinationGroup'] as const).filter((k) => s[k] !== 'All'),
  ];
  const keys = d.conversion ? all.filter((k) => !SALES_ONLY_KEYS.includes(k)) : all;
  return [
    ...keys.map((k) => k + '=' + s[k]),
    'source=' + d.source,
    'observations=' + d.trendStart + '..' + d.end,
    'summary=' + d.start + '..' + d.summaryEnd,
    'dataQueried=' + (d.queriedAt?.replace(/^Data queried /, '') ?? 'Unknown'),
  ];
}
export function exportCountries(s: State, d: Dashboard): string {
  if (!d.ready || !d.valid || d.stateIdentity !== stateSearch(s)) {
    throw new Error('Countries data is not ready for export.');
  }
  const unit = d.conversion ? 'ratio' : ['orders', 'customers'].includes(s.metric) ? 'count' : 'A$';
  // CR values are rates; every other chart value is money or a moving-average count.
  const valueDecimals = d.conversion ? RATIO_DECIMALS : MONEY_DECIMALS;
  const rows: Array<Array<unknown>> = [
    ['Scope', ...exportScope(s, d)],
    [
      'View',
      'Panel',
      'Sydney date',
      d.conversion ? 'CR packages/views' : 'Current value',
      'LY',
      'YoY',
      'Raw current',
      'Raw LY',
      'Packages',
      'Views',
      'Unit',
    ],
    ...d.kpis.map((k) => [
      s.view,
      'Summary ' + k.label,
      d.summaryEnd,
      rounded(k.current, MONEY_DECIMALS),
      rounded(k.previous, MONEY_DECIMALS),
      rounded(k.growth, RATIO_DECIMALS),
      null,
      null,
      null,
      null,
      ['orders', 'customers'].includes(k.metric) ? 'count' : 'A$',
    ]),
  ];
  for (const panel of [{ name: 'Overall', points: d.overview }, ...d.panels]) {
    for (const p of panel.points) {
      rows.push([
        s.view,
        panel.name,
        p.date,
        rounded(p.current, valueDecimals),
        rounded(p.previous, valueDecimals),
        rounded(p.yoy, RATIO_DECIMALS),
        rounded(p.raw, valueDecimals),
        rounded(p.rawLY, valueDecimals),
        p.packages ?? null,
        p.views ?? null,
        unit,
      ]);
    }
  }
  return '\uFEFF' + rows.map((row) => row.map((v) => csvCell(v, 'Unknown')).join(',')).join('\r\n') + '\r\n';
}
